/**
 * AssistantMetaChip — admin-only per-message telemetry chip.
 *
 * Renders N tokens · $cost · model · latency on completed assistant
 * messages. Hidden for non-admins so the regular chat surface stays
 * clean. Driven entirely by props — no data fetching.
 */

import type { Message } from '../../App';
import './AssistantMetaChip.css';

type Props = {
    message: Message;
};

function formatLatency(ms: number | null | undefined): string {
    if (ms == null) return '—';
    if (ms < 1000) return `${ms} ms`;
    return `${(ms / 1000).toFixed(1)}s`;
}

function formatCost(raw: string | null | undefined): string {
    if (!raw) return '—';
    const n = Number.parseFloat(raw);
    if (!Number.isFinite(n) || n === 0) return '$0';
    if (n < 0.0001) return '<$0.0001';
    if (n < 0.01) return `$${n.toFixed(4)}`;
    return `$${n.toFixed(3)}`;
}

export default function AssistantMetaChip({ message }: Props) {
    if (message.role !== 'assistant') return null;
    if (message.streaming) return null;
    const usage = message.usage;
    if (!usage || !usage.total_tokens) return null;

    return (
        <div
            className="assistant-meta-chip"
            role="note"
            aria-label="Assistant turn telemetry (admin-only)"
        >
            <span>{usage.total_tokens.toLocaleString()} tokens</span>
            {message.cost_usd && <span>· {formatCost(message.cost_usd)}</span>}
            {message.model && <span>· {message.model}</span>}
            {message.latency_ms != null && <span>· {formatLatency(message.latency_ms)}</span>}
        </div>
    );
}
